/**
 * Pipeline registry — the two loan pipelines this portal serves.
 *
 * The backend deliberately mirrors the underwriting endpoint surface for
 * acquisitions (28 of its 44 routes are name-for-name identical; only the router
 * prefix differs — see eastview-os frontend_documentation/loan-acquisitions.md).
 * So instead of forking the drilldown, we inject the API prefix + a capability
 * map through PipelineContext and reuse one set of components for both.
 *
 * - UNDERWRITING (preclose): loans closing THROUGH us. `/underwriting/*`
 * - ACQUISITIONS (postclose): already-closed loans PURCHASED from correspondents,
 *   traded in pools. `/loan_acquisitions/*`
 *
 * `capabilities` drives which tabs/panels render, and which sub-API a shared
 * feature routes to where the two pipelines genuinely diverge (conditions and
 * task lists live in the /checklists router for preclose, but on the pipeline's
 * own router for acquisitions).
 */
export const PIPELINE_KEYS = {
  UNDERWRITING: "underwriting",
  ACQUISITIONS: "acquisitions",
};

// Where a shared feature's endpoints live for a given pipeline.
export const API_SOURCE = {
  CHECKLISTS: "checklists", // /checklists/* (preclose conditions + task lists)
  PIPELINE: "pipeline", // the pipeline's own router (/loan_acquisitions/*)
};

export const PIPELINES = {
  [PIPELINE_KEYS.UNDERWRITING]: {
    key: PIPELINE_KEYS.UNDERWRITING,
    label: "Underwriting",
    // Route prefix consumed by useUnderwritingHook (all 56 call sites).
    apiPrefix: "underwriting",
    // FE routes
    pipelinePath: "/loans",
    drilldownBase: "/loan",
    // The pipeline's own list route under apiPrefix. NOT the same name in both:
    // GET /underwriting/underwriting_pipeline vs
    // GET /loan_acquisitions/acquisitions_pipeline.
    pipelineListPath: "underwriting_pipeline",
    // The notes pane. NOTE: acquisitions renames only the PATH segment
    // (/loan_acquisitions/diligence_notes/{dealId}) — its handler still writes
    // the deal's `underwritingNotes` field, so the read field is shared.
    notesPath: "underwritingNotes",
    notesLabel: "Underwriting Notes",
    notesField: "underwritingNotes",
    capabilities: {
      // preclose-only surfaces
      creditPull: true,
      borrowerPortal: true,
      borrowerSubmissions: true,
      loanTerms: true,
      fees: true,
      loanLock: true,
      // The preclose UW task-list tab (templates → per-deal tasklist by id, with
      // per-task updates + comments). Acquisitions models task lists completely
      // differently (repository-derived template + a deal-keyed completion doc,
      // no taskListId), so that tab can't be reused — it needs its own build.
      precloseChecklist: true,
      // postclose-only surfaces
      tradeEconomics: false,
      closedPackage: false,
      performance: false,
      bulkTrade: false,
      // where shared features resolve their endpoints
      conditionsApi: API_SOURCE.CHECKLISTS,
      taskListApi: API_SOURCE.CHECKLISTS,
    },
  },

  [PIPELINE_KEYS.ACQUISITIONS]: {
    key: PIPELINE_KEYS.ACQUISITIONS,
    label: "Acquisitions",
    apiPrefix: "loan_acquisitions",
    pipelinePath: "/acquisitions",
    drilldownBase: "/acquisition",
    pipelineListPath: "acquisitions_pipeline",
    notesPath: "diligence_notes",
    notesLabel: "Diligence Notes",
    // Not a typo: the diligence_notes handler $sets `underwritingNotes` on the
    // acquisition deal doc, so the field to READ is the same in both pipelines.
    notesField: "underwritingNotes",
    capabilities: {
      // Excluded by design (postclose): credit pull, borrower submissions,
      // borrower-portal linking. No application fees — costs ride the trade.
      creditPull: false,
      borrowerPortal: false,
      borrowerSubmissions: false,
      loanTerms: false,
      fees: false,
      // Loan Lock IS shown for acquisition loans (the deal.loanLock object is
      // the same field in both pipelines; the tab keeps its UW/admin gate).
      loanLock: true,
      precloseChecklist: false,
      // The postclose surfaces
      tradeEconomics: true,
      closedPackage: true,
      performance: true,
      bulkTrade: true,
      conditionsApi: API_SOURCE.PIPELINE,
      taskListApi: API_SOURCE.PIPELINE,
    },
  },
};

/**
 * Postclose trade lifecycle. Drives the status tab strip on the Acquisitions
 * pipeline table, the same way PIPELINE_TABS statuses drive Pre-Close.
 *
 * Copied VERBATIM, in order, from `acquisition_status_options` in
 * eastview-os/app/models/loan_acquisitions.py (the model is source of truth;
 * there is no /model_schemas object type for acquisitions to read it from).
 * 2026-10-01: Condition Clearance + Final Review follow Due Diligence.
 * 2026-10-02: Inactive = on hold (the SF On_Hold__c flag), not a milestone.
 * (Repurchase Review was removed from the model on 2026-09-18.)
 */
export const ACQUISITION_STATUSES = [
  "New Trade",
  "Trade Review",
  "Due Diligence",
  "Condition Clearance",
  "Final Review",
  "Cleared to Purchase",
  "Funding",
  "Settled",
  "Boarded",
  "Kicked",
  "Declined",
  "Inactive",
];

/** The acquisition on-hold status (SF On_Hold__c ↔ LOS `Inactive`). */
export const ACQUISITION_ON_HOLD_STATUS = "Inactive";

/**
 * Salesforce EV Post Close path stage → LOS acquisition status. Copied verbatim
 * from STAGE_MAP in eastview-os/app/models/salesforce_to_los_acquisitions_sync.py.
 * An Inactive (on hold) acquisition keeps the SF stage it was held at in
 * `subStatus`, and un-holding returns it to the status that stage maps to — so
 * this is how the UI knows where a held loan sits and where "Resume" goes.
 */
const ACQUISITION_SF_STAGE_TO_STATUS = {
  Lead: "New Trade",
  Underwriting: "Trade Review",
  "Condition Clearing": "Condition Clearance",
  "Underwriter review": "Due Diligence",
  "Final Review": "Final Review",
  "Clear to Purchase": "Cleared to Purchase",
  Processing: "Trade Review",
  "Clear To Close": "Cleared to Purchase",
  Won: "Settled",
  Lost: "Declined",
  Funded: "Settled",
};

/**
 * The milestone an on-hold acquisition was held at (and resumes to), or null
 * when the loan isn't on hold or its `subStatus` doesn't name a known stage.
 * `subStatus` may already be a LOS status (a hold set from the LOS keeps the
 * prior subStatus), so a value that is itself a milestone is taken as-is.
 */
export const acquisitionHeldStatus = (loan) => {
  if (loan?.loanStatus !== ACQUISITION_ON_HOLD_STATUS) return null;
  const sub = loan?.subStatus;
  if (!sub) return null;
  if (ACQUISITION_SF_STAGE_TO_STATUS[sub]) {
    return ACQUISITION_SF_STAGE_TO_STATUS[sub];
  }
  return ACQUISITION_STATUSES.includes(sub) && sub !== ACQUISITION_ON_HOLD_STATUS
    ? sub
    : null;
};

export const DEFAULT_PIPELINE = PIPELINES[PIPELINE_KEYS.UNDERWRITING];

export const getPipeline = (key) => PIPELINES[key] || DEFAULT_PIPELINE;

/**
 * Drilldown route base for one pipeline ROW. Boards that mix pipelines (the Full
 * Pipeline) tag each row with `_pipelineKey`, so an acquisition opens at
 * /acquisition/:id and a preclose loan at /loan/:id. Untagged rows fall back to
 * the board's own base.
 */
export const drilldownBaseForRow = (
  row,
  fallback = DEFAULT_PIPELINE.drilldownBase,
) =>
  row?._pipelineKey ? getPipeline(row._pipelineKey).drilldownBase : fallback;
